import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Search, FileText, CheckCircle2, CircleHelp, CircleOff, Download } from "lucide-react";
import { tarifasApi, errMsg, fmtMoney, fmtDate, tierLabel } from "./api";

const STATE = {
  confirmed: ["En KimKim", "bg-emerald-100 text-emerald-800", CheckCircle2],
  proposed: ["Propuesta por revisar", "bg-amber-100 text-amber-800", CircleHelp],
  none: ["No está en KimKim", "bg-slate-100 text-slate-600", CircleOff],
};

const UNIT = { per_person: "por persona", per_group: "por grupo", per_service: "por servicio", per_unit: "por unidad", per_hour: "por hora" };

const rowLabel = (r) => {
  const min = r.min_pax ?? 1;
  const max = r.max_pax == null ? "+" : r.max_pax;
  return min === max || (r.max_pax == null && min === 1) ? (r.max_pax == null ? `${min}+` : `${min}`) : `${min}-${max}`;
};

const NetPrices = ({ rows }) => (
  <div className="flex flex-wrap gap-1" data-testid="net-prices">
    {rows.map((r, i) => (
      <span key={i} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-mono ${r.unreadable || r.amount == null ? "border-amber-300 bg-amber-50 text-amber-800" : "bg-white"}`} title={[r.season, r.notes, r.supplements].filter(Boolean).join(" · ")}>
        <span className="text-muted-foreground">{rowLabel(r)}</span>
        {r.amount != null ? fmtMoney(r.amount, r.currency || "") : (r.amount_raw || "consultar")}
        {r.season && <span className="text-[10px] text-muted-foreground">{r.season}</span>}
        {r.age_category && !["all", "adult", "adults"].includes(String(r.age_category).toLowerCase()) && <span className="text-[10px] text-muted-foreground">{r.age_category}</span>}
      </span>
    ))}
  </div>
);

const KimkimCell = ({ item, onOpenFile }) => {
  if (!item.kimkim.length) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="space-y-1">
      {item.kimkim.map((l) => {
        const changed = l.rows.filter((r) => r.changed && r.in_tariff && r.in_kimkim);
        return (
          <div key={l.correspondence_id} className="text-xs" data-testid={`tarifa-kk-${l.correspondence_id}`}>
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-medium">{l.kimkim_title}</span>
              <a href={l.kimkim_url} target="_blank" rel="noreferrer" className="text-primary underline flex items-center gap-0.5">KimKim <ExternalLink className="h-3 w-3" /></a>
              {l.status !== "confirmed" && <Badge className="rounded-md bg-amber-50 text-amber-800 hover:bg-amber-50">{l.status === "proposed" ? "propuesta IA" : "pendiente"}</Badge>}
              {l.proposal_status === "applied" && <Badge className="rounded-md bg-emerald-200 text-emerald-900 hover:bg-emerald-200">actualizada {fmtDate(l.applied_at)}</Badge>}
              {l.price_status === "ready" && <Badge className="rounded-md bg-sky-100 text-sky-800 hover:bg-sky-100">{changed.length} importes cambiarían</Badge>}
              {l.price_status === "no_change" && <Badge className="rounded-md bg-slate-100 text-slate-600 hover:bg-slate-100">precios al día</Badge>}
              {l.price_status === "pending" && l.rows.length === 0 && <Badge className="rounded-md bg-amber-50 text-amber-800 hover:bg-amber-50" title={l.reasons?.[0]}>revisar</Badge>}
            </div>
            {l.rows.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-0.5">
                {l.rows.filter((r) => r.in_tariff || (r.in_kimkim && r.current > 0)).map((r) => (
                  <span key={`${r.min}-${r.max}`} className={`rounded-md border px-1.5 py-0.5 font-mono ${r.changed ? "border-emerald-300 bg-emerald-50" : "bg-white"}`}>
                    <span className="text-muted-foreground">{tierLabel(r.min, r.max)} </span>
                    {r.in_kimkim ? fmtMoney(r.current, "") : <span className="text-amber-700">—</span>}
                    {r.in_tariff && <span className={r.changed ? "text-emerald-700" : "text-muted-foreground"}> → {fmtMoney(r.computed, "")}</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <button className="text-[11px] underline text-muted-foreground" onClick={() => onOpenFile(item.file_id)} data-testid={`tarifa-open-file-${item.key}`}>revisar / actualizar en el archivo</button>
    </div>
  );
};

export const ProviderTarifas = ({ provider, onBack, onOpenFile }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [state, setState] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await tarifasApi.providerTarifas(provider.id));
    } catch (e) {
      toast.error(errMsg(e, "No se pudieron cargar las tarifas"));
    } finally {
      setLoading(false);
    }
  }, [provider.id]);

  useEffect(() => { load(); }, [load]);

  const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const RANK = { confirmed: 0, proposed: 1, none: 2 };
  const items = (data?.items || [])
    .filter((it) => (state === "all" || it.kimkim_state === state) && (!q || norm(it.name).includes(norm(q)) || norm(it.code).includes(norm(q)) || it.kimkim.some((l) => norm(l.kimkim_title).includes(norm(q)))))
    .sort((a, b) => RANK[a.kimkim_state] - RANK[b.kimkim_state] || a.name.localeCompare(b.name, "es"));
  const s = data?.summary;

  return (
    <Card className="rounded-md" data-testid="provider-tarifas">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button data-testid="provider-tarifas-back" size="sm" variant="ghost" className="rounded-md" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" /> Proveedores</Button>
            <div>
              <h3 className="font-heading text-base font-bold">Tarifas de {provider.name}</h3>
              <p className="text-xs text-muted-foreground">{[provider.country, provider.destination].filter(Boolean).join(" · ")} · alias: {(provider.aliases || []).join(", ") || "—"} · precios {provider.price_basis === "net" ? "netos" : provider.price_basis === "public" ? "públicos" : "según documento"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data?.files?.length > 0 && (
              <Button data-testid="provider-tarifas-export" size="sm" className="rounded-md" asChild>
                <a href={tarifasApi.providerExportUrl(provider.id)} download><Download className="h-3 w-3 mr-1" /> Descargar Excel</a>
              </Button>
            )}
            <Button data-testid="provider-tarifas-refresh" size="sm" variant="outline" className="rounded-md" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}</Button>
          </div>
        </div>
        {s && data.files.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-2 text-xs" data-testid="provider-tarifas-summary">
            <Badge className="rounded-md bg-slate-100 text-slate-700 hover:bg-slate-100">{s.activities} actividades en el tarifario</Badge>
            <Badge className="rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{s.in_kimkim} en KimKim</Badge>
            <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100">{s.proposed} propuestas por revisar</Badge>
            <Badge className="rounded-md bg-slate-100 text-slate-600 hover:bg-slate-100">{s.not_in_kimkim} no están en KimKim</Badge>
            {s.applied > 0 && <Badge className="rounded-md bg-emerald-200 text-emerald-900 hover:bg-emerald-200">{s.applied} actualizadas en KimKim</Badge>}
            <span className="text-muted-foreground ml-auto flex items-center gap-1"><FileText className="h-3 w-3" /> {data.files.map((f) => f.web_view_link ? <a key={f.id} href={f.web_view_link} target="_blank" rel="noreferrer" className="underline">{f.name}</a> : <span key={f.id}>{f.name}</span>).reduce((acc, el, i) => (i ? [...acc, " · ", el] : [el]), [])}</span>
          </div>
        )}
        {data && data.files.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input data-testid="provider-tarifas-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar actividad…" className="h-8 w-64 pl-7 rounded-md text-xs" />
          </div>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="provider-tarifas-state" className="h-8 w-56 rounded-md text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="confirmed">En KimKim</SelectItem>
              <SelectItem value="proposed">Propuestas por revisar</SelectItem>
              <SelectItem value="none">No están en KimKim</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {!data && <p className="text-sm text-muted-foreground py-3">Cargando…</p>}
        {data && data.files.length === 0 && <p className="text-sm text-muted-foreground py-3" data-testid="provider-tarifas-empty">Este proveedor aún no tiene ningún tarifario procesado. Procesa su archivo en la pestaña Archivos.</p>}
        {data && data.files.length > 0 && items.length === 0 && <p className="text-sm text-muted-foreground py-3">Ninguna actividad coincide con el filtro.</p>}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="provider-tarifas-table">
              <thead className="text-xs text-muted-foreground">
                <tr className="text-left">
                  <th className="py-2 pr-3 font-medium">Actividad (nombre del proveedor)</th>
                  <th className="pr-3 font-medium">Precio neto</th>
                  <th className="pr-3 font-medium">KimKim</th>
                  <th className="font-medium">Actividad KimKim · ahora → neto + 8 %</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const [label, cls, Icon] = STATE[it.kimkim_state];
                  const units = [...new Set(it.price_rows.map((r) => r.unit))];
                  return (
                    <tr key={`${it.file_id}-${it.key}`} className="border-t align-top" data-testid={`tarifa-row-${it.key}`}>
                      <td className="py-2 pr-3 max-w-xs">
                        <div className="font-medium">{it.name}{it.code && <span className="font-mono text-xs text-muted-foreground"> [{it.code}]</span>}</div>
                        <div className="text-xs text-muted-foreground">{[it.modality === "private" ? "privada" : it.modality === "shared" ? "compartida" : null, it.duration, units.map((u) => UNIT[u] || u).join("/")].filter(Boolean).join(" · ")}</div>
                        {it.notes && <div className="text-[11px] text-muted-foreground line-clamp-2" title={it.notes}>{it.notes}</div>}
                      </td>
                      <td className="py-2 pr-3"><NetPrices rows={it.price_rows} /></td>
                      <td className="py-2 pr-3 whitespace-nowrap"><Badge className={`rounded-md ${cls} hover:${cls.split(" ")[0]}`} data-testid={`tarifa-state-${it.key}`}><Icon className="h-3 w-3 mr-1" /> {label}</Badge></td>
                      <td className="py-2"><KimkimCell item={it} onOpenFile={onOpenFile} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
